import React, { useEffect, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { fetchCallRiskValues } from 'services/hvarisk.service';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faCheckCircle,
  faUserCheck
} from '@fortawesome/free-solid-svg-icons';
import Badge from 'components/base/Badge';
import HVASpinner from 'components/base/HVASpinner';

interface CallRiskAnalysisProps {
  callid: string;
  campaignid: number;
  componentHeight?: string;
}

interface RiskValue {
  id: number;
  callid: string;
  riskelementid: number;
  iariskvalue: number;
  iareasoning?: string;
  active: boolean;
  qariskvalue?: number;
  qaagent?: number;
  qaagentcoment?: string;
  iariskvaluedate: string;
  qariskvaluedate?: string;
  hvariskelement?: {
    id: number;
    name: string;
    description?: string;
    hvariskcategory?: {
      id: number;
      name: string;
    };
  };
  reviewer?: {
    name: string;
    lastname: string;
  };
}

interface GroupedRisks {
  [categoryName: string]: RiskValue[];
}

export const CallRiskAnalysis: React.FC<CallRiskAnalysisProps> = ({
  callid,
  campaignid,
  componentHeight
}) => {
  const [risks, setRisks] = useState<RiskValue[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRisks = async () => {
      try {
        setIsLoading(true);
        const data = await fetchCallRiskValues(callid, campaignid);
        setRisks(data);
        setError(null);

        // Expandir por defecto las categorías con riesgos de alta severidad
        const defaultExpanded: { [key: string]: boolean } = {};
        data.forEach((risk: RiskValue) => {
          if (risk.iariskvalue >= 4) {
            const categoryName =
              risk.hvariskelement?.hvariskcategory?.name || 'Uncategorized';
            defaultExpanded[categoryName] = true;
          }
        });
      } catch (err) {
        console.error('Error fetching risk values:', err);
        setError('Error loading risk analysis');
        setRisks([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (callid && campaignid) {
      fetchRisks();
    }
  }, [callid, campaignid]);

  // Agrupar riesgos por categoría
  const groupedRisks = risks.reduce<GroupedRisks>((acc, risk) => {
    const categoryName =
      risk.hvariskelement?.hvariskcategory?.name || 'Uncategorized';
    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }
    acc[categoryName].push(risk);
    return acc;
  }, {});

  // Función para determinar el color del badge según el valor de riesgo
  const getRiskBadgeVariant = (
    value: number
  ): 'success' | 'warning' | 'danger' => {
    if (value <= 2) return 'success';
    if (value === 3) return 'warning';
    return 'danger';
  };

  // Función para obtener el texto del nivel de riesgo
  const getRiskLevelText = (value: number): string => {
    if (value <= 2) return 'Low';
    if (value === 3) return 'Medium';
    if (value === 4) return 'High';
    return 'Critical';
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-4">
        <HVASpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="fs-9 mb-2">
        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
        {error}
      </div>
    );
  }

  if (risks.length === 0) {
    return (
      <div className="text-muted fs-9">
        <FontAwesomeIcon icon={faCheckCircle} className="me-2 text-success" />
        No risks analized in this call.
      </div>
    );
  }

  // Contar riesgos de alta severidad
  const highSeverityCount = risks.filter(r => r.iariskvalue >= 4).length;
  const mediumSeverityCount = risks.filter(r => r.iariskvalue === 3).length;
  const lowSeverityCount = risks.filter(r => r.iariskvalue <= 2).length;
  const activeCount = risks.filter(r => r.active).length;

  return (
    <div>
      {/* Resumen de riesgos */}
      <div className="d-flex p-2 px-3 gap-3 mb-3 flex-wrap bg-body-highlight rounded">
        <div className="fs-9">
          Analized <span className="fw-bold">{risks.length}</span> risk
          elements:
        </div>
        <div className="fs-9">
          <span className="text-muted">Low:</span>{' '}
          <Badge
            pill
            bg="success"
            style={{
              minWidth: '35px'
            }}
          >
            {lowSeverityCount}
          </Badge>
        </div>
        <div className="fs-9">
          <span className="text-muted">Medium:</span>{' '}
          <Badge
            pill
            bg="warning"
            style={{
              minWidth: '35px'
            }}
          >
            {mediumSeverityCount}
          </Badge>
        </div>
        <div className="fs-9">
          <span className="text-muted">High:</span>{' '}
          <Badge
            pill
            bg="danger"
            style={{
              minWidth: '35px'
            }}
          >
            {highSeverityCount}
          </Badge>
        </div>
        {activeCount > 0 && (
          <div className="fs-9">
            <span className="text-muted">Pending review:</span>{' '}
            <Badge pill variant="phoenix" bg="warning">
              {activeCount}
            </Badge>
          </div>
        )}
      </div>

      {/* Riesgos agrupados por categoría */}
      <div
        className="fs-9 scrollbar"
        style={{
          maxHeight: !componentHeight ? '258px' : componentHeight,
          height: !componentHeight ? '258px' : componentHeight
        }}
      >
        {Object.entries(groupedRisks).map(
          ([category, categoryRisks], index) => {
            const hasHighRisk = categoryRisks.some(r => r.iariskvalue >= 4);

            return (
              <div key={index}>
                <div className="p-0">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-semibold">{category}</span>
                      <Badge variant="phoenix" bg="secondary" className="ms-2">
                        {categoryRisks.length}
                      </Badge>
                      {hasHighRisk && (
                        <FontAwesomeIcon
                          icon={faExclamationTriangle}
                          className="text-danger"
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="p-2 pt-1 pb-1 pe-1 mb-3">
                  {categoryRisks.map(risk => {
                    const finalValue = risk.qariskvalue || risk.iariskvalue;
                    const isReviewed = risk.qariskvalue !== null;

                    return (
                      <div
                        key={risk.id}
                        className="rounded p-2 pb-1 pt-1 pe-1 mb-2 bg-body"
                      >
                        {/* Encabezado del riesgo */}
                        <div className="d-flex justify-content-between align-items-start mb-0">
                          <div className="flex-grow-1">
                            <p className="mb-0 fw-bold fs-10">
                              {risk.hvariskelement?.name}
                            </p>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            {isReviewed && (
                              <FontAwesomeIcon
                                icon={faUserCheck}
                                className="text-primary"
                                title="Human reviewed"
                              />
                            )}
                            <Badge
                              variant="phoenix"
                              bg={getRiskBadgeVariant(finalValue)}
                            >
                              {getRiskLevelText(finalValue)} ({finalValue})
                            </Badge>
                          </div>
                        </div>

                        {/* Análisis de IA */}
                        <div className="mb-0">
                          <div className="d-flex align-items-top gap-1 mb-0 pe-10 lh-1">
                            <span className="fw-semibold fs-10">
                              AI Analysis:
                              {` (${new Date(
                                risk.iariskvaluedate
                              ).toLocaleString()}) `}
                              <span className="fw-normal fs-10">
                                {risk.iareasoning ||
                                  'No detailed analysis available'}
                              </span>
                            </span>
                          </div>
                        </div>

                        {/* Revisión humana si existe */}
                        {isReviewed && (
                          <div className="border-top pt-2 mt-2">
                            <div className="d-flex align-items-center gap-1 mb-1">
                              <FontAwesomeIcon
                                icon={faUserCheck}
                                className="text-primary fs-10"
                              />
                              <span className="fw-semibold fs-10">
                                Human Review:
                              </span>
                            </div>
                            <p className="mb-1 ms-3 fs-10">
                              <strong>Adjusted severity:</strong>{' '}
                              {risk.qariskvalue} (
                              {getRiskLevelText(risk.qariskvalue!)})
                            </p>
                            {risk.qaagentcoment && (
                              <p className="mb-1 ms-3 fs-10">
                                <strong>Comment:</strong> {risk.qaagentcoment}
                              </p>
                            )}
                            <p className="mb-0 ms-3 text-muted fs-10">
                              Reviewed by: {risk.reviewer?.name}{' '}
                              {risk.reviewer?.lastname}
                              {risk.qariskvaluedate &&
                                ` on ${new Date(
                                  risk.qariskvaluedate
                                ).toLocaleString()}`}
                            </p>
                          </div>
                        )}

                        {/* Indicador de pendiente de revisión */}
                        {risk.active && (
                          <Alert
                            className="mt-2 mb-0 py-1 px-2 fs-10"
                            variant="subtle-warning"
                          >
                            <FontAwesomeIcon
                              icon={faExclamationTriangle}
                              className="me-1"
                            />
                            Pending human review
                          </Alert>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          }
        )}
      </div>
    </div>
  );
};
